import { HStack } from '@/components/ui/hstack';
import { Skeleton } from '@/components/ui/skeleton';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * Placeholder message bubbles, shaped like the thread they stand in for: a day chip, then
 * alternating received and sent bubbles. They sit at the bottom, against the composer, because
 * that is where a short thread's messages land once it loads; bubbles at the top of an empty
 * screen would jump to the bottom as soon as the real ones replaced them.
 */
function MessagesSkeleton() {
  return (
    <VStack className="flex-1 justify-end py-2" space="lg">
      <Skeleton variant="circular" className="h-7 w-28 self-center" />

      <HStack className="items-end" space="xs">
        <Skeleton variant="circular" className="h-8 w-8" />
        <Skeleton className="h-16 w-2/3 rounded-2xl" />
      </HStack>

      <HStack className="justify-end">
        <Skeleton className="h-12 w-1/2 rounded-2xl" />
      </HStack>

      <HStack className="items-end" space="xs">
        <Skeleton variant="circular" className="h-8 w-8" />
        <Skeleton className="h-24 w-3/4 rounded-2xl" />
      </HStack>

      <HStack className="justify-end">
        <Skeleton className="h-10 w-2/5 rounded-2xl" />
      </HStack>
    </VStack>
  );
}

/**
 * The chat while it loads, below the header: messages against the bottom and, where the inquiry
 * can still be replied to, a block where the composer will be. The screen shows it while the
 * conversation loads and the chat keeps it until the first page of messages arrives, so the
 * reader sees one placeholder throughout. `showActionBar` holds the place of the row above the
 * composer, such as an expert's "On your honor", at the size and gap the real one takes. `bottomInset` is the chrome the composer rides above,
 * such as the tab bar, so the composer block lands about where the composer does.
 */
export function ChatScreenSkeleton({
  showComposer,
  showActionBar = false,
  bottomInset,
}: {
  showComposer: boolean;
  showActionBar?: boolean;
  bottomInset: number;
}) {
  return (
    <VStack
      className="flex-1 px-2.5"
      space="md"
      style={{ paddingBottom: bottomInset + 8 }}
      {...getAccessibilityProps({ label: 'Loading conversation', role: 'progressbar' })}>
      <MessagesSkeleton />

      {showComposer ? (
        <VStack space="sm">
          {showActionBar ? <Skeleton className="h-6 w-32 self-end rounded-full" /> : null}
          <Skeleton className="h-[90px] rounded-3xl" />
        </VStack>
      ) : null}
    </VStack>
  );
}
